import { create } from "zustand";

const STORAGE_KEY = "magic-md:pinned-documents";
const MAX_PINNED_DOCUMENTS = 24;

export interface PinnedDocument {
    id: string;
    path: string;
    title: string;
    pinnedAt: number;
    lastOpenedAt?: number;
}

interface PinDocumentInput {
    path: string;
    title?: string;
}

interface PinnedDocumentsState {
    items: PinnedDocument[];
    pinDocument: (input: PinDocumentInput) => PinnedDocument | null;
    unpinDocument: (path: string) => void;
    touchDocument: (path: string) => void;
    clearPinnedDocuments: () => void;
    isPinned: (path: string) => boolean;
    replaceFromStorage: (items: PinnedDocument[]) => void;
}

function storageAvailable(): boolean {
    return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function titleFromPinnedDocumentPath(path: string): string {
    const normalized = path.replace(/\\/g, "/");
    return normalized.split("/").filter(Boolean).pop() ?? path;
}

export function pinnedDocumentIdFromPath(path: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < path.length; i += 1) {
        hash ^= path.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return `doc-${(hash >>> 0).toString(36)}`;
}

function isValidPinnedDocument(value: unknown): value is PinnedDocument {
    if (!value || typeof value !== "object") return false;
    const item = value as Partial<PinnedDocument>;
    return (
        typeof item.id === "string" &&
        typeof item.path === "string" &&
        item.path.length > 0 &&
        typeof item.title === "string" &&
        typeof item.pinnedAt === "number"
    );
}

export function normalizePinnedDocuments(items: PinnedDocument[]): PinnedDocument[] {
    const seen = new Set<string>();
    const normalized: PinnedDocument[] = [];

    for (const item of items) {
        const path = item.path.trim();
        if (!path || seen.has(path)) continue;
        seen.add(path);
        normalized.push({
            id: item.id || pinnedDocumentIdFromPath(path),
            path,
            title: item.title || titleFromPinnedDocumentPath(path),
            pinnedAt: Number.isFinite(item.pinnedAt) ? item.pinnedAt : Date.now(),
            ...(typeof item.lastOpenedAt === "number"
                ? { lastOpenedAt: item.lastOpenedAt }
                : {}),
        });
    }

    return normalized
        .sort((a, b) => a.pinnedAt - b.pinnedAt)
        .slice(0, MAX_PINNED_DOCUMENTS);
}

function loadPinnedDocuments(): PinnedDocument[] {
    if (!storageAvailable()) return [];
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        return readPinnedDocumentsFromStorageValue(raw);
    } catch {
        return [];
    }
}

function savePinnedDocuments(items: PinnedDocument[]): void {
    if (!storageAvailable()) return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
        /* ignore persistence failures */
    }
}

export const usePinnedDocumentsStore = create<PinnedDocumentsState>((set, get) => ({
    items: loadPinnedDocuments(),

    pinDocument: ({ path, title }: PinDocumentInput) => {
        const trimmedPath = path.trim();
        if (!trimmedPath) return null;

        const now = Date.now();
        const existing = get().items.find((item) => item.path === trimmedPath);
        const pinned: PinnedDocument = existing
            ? {
                ...existing,
                title: title || existing.title || titleFromPinnedDocumentPath(trimmedPath),
            }
            : {
                id: pinnedDocumentIdFromPath(trimmedPath),
                path: trimmedPath,
                title: title || titleFromPinnedDocumentPath(trimmedPath),
                pinnedAt: now,
            };

        const next = normalizePinnedDocuments([
            ...get().items.filter((item) => item.path !== trimmedPath),
            pinned,
        ]);
        savePinnedDocuments(next);
        set({ items: next });
        return pinned;
    },

    unpinDocument: (path: string) => {
        const next = get().items.filter((item) => item.path !== path);
        savePinnedDocuments(next);
        set({ items: next });
    },

    touchDocument: (path: string) => {
        const now = Date.now();
        const next = get().items.map((item) =>
            item.path === path ? { ...item, lastOpenedAt: now } : item,
        );
        savePinnedDocuments(next);
        set({ items: next });
    },

    clearPinnedDocuments: () => {
        savePinnedDocuments([]);
        set({ items: [] });
    },

    isPinned: (path: string) => get().items.some((item) => item.path === path),

    replaceFromStorage: (items: PinnedDocument[]) => {
        set({ items: normalizePinnedDocuments(items) });
    },
}));

export function readPinnedDocumentsFromStorageValue(value: string | null): PinnedDocument[] {
    if (!value) return [];
    try {
        const parsed: unknown = JSON.parse(value);
        if (!Array.isArray(parsed)) return [];
        return normalizePinnedDocuments(parsed.filter(isValidPinnedDocument));
    } catch {
        return [];
    }
}

export function getPinnedDocumentsStorageKey(): string {
    return STORAGE_KEY;
}
